// Free, public, no-key-required pincode lookup via India Post's API.
// Used on PDP + cart to give customers immediate "yes we deliver to your
// area" confirmation, with an ETA mapped from state.
//
// Endpoint: https://api.postalpincode.in/pincode/{code}
// Response: [{ Status: "Success" | "Error", PostOffice: [...] }]
// We treat any "Success" with at least one PostOffice having
// DeliveryStatus !== "Non Delivery" as serviceable.

const ENDPOINT = (code) => `https://api.postalpincode.in/pincode/${encodeURIComponent(code)}`;

// State -> typical delivery window from our Hyderabad warehouse. Tuned to
// match the Shipping Policy page wording so the customer sees consistent
// numbers in the PDP widget and the policy.
const STATE_ETA = {
  'Telangana':        { min: 1, max: 2 },
  'Andhra Pradesh':   { min: 2, max: 3 },
  // Major metros
  'Maharashtra':      { min: 2, max: 4 },
  'Karnataka':        { min: 2, max: 4 },
  'Tamil Nadu':       { min: 2, max: 4 },
  'Delhi':            { min: 2, max: 4 },
  'Haryana':          { min: 2, max: 4 },
  'Uttar Pradesh':    { min: 3, max: 5 },
  'West Bengal':      { min: 3, max: 5 },
  'Gujarat':          { min: 3, max: 5 },
  'Kerala':           { min: 3, max: 5 },
  'Madhya Pradesh':   { min: 3, max: 5 },
  'Rajasthan':        { min: 3, max: 5 },
  'Punjab':           { min: 3, max: 5 },
  'Odisha':           { min: 3, max: 5 },
  'Bihar':            { min: 4, max: 6 },
  'Jharkhand':        { min: 4, max: 6 },
  'Chhattisgarh':     { min: 3, max: 5 },
  'Uttarakhand':      { min: 4, max: 6 },
  'Himachal Pradesh': { min: 4, max: 7 },
  // Remote / North-East / island states
  'Jammu and Kashmir':           { min: 5, max: 10 },
  'Ladakh':                      { min: 7, max: 12 },
  'Assam':                       { min: 5, max: 8 },
  'Meghalaya':                   { min: 5, max: 9 },
  'Manipur':                     { min: 6, max: 10 },
  'Mizoram':                     { min: 6, max: 10 },
  'Nagaland':                    { min: 6, max: 10 },
  'Tripura':                     { min: 6, max: 10 },
  'Arunachal Pradesh':           { min: 7, max: 12 },
  'Sikkim':                      { min: 6, max: 10 },
  'Andaman and Nicobar Islands': { min: 8, max: 14 },
  'Lakshadweep':                 { min: 8, max: 14 },
  'Goa':                         { min: 3, max: 5 },
  'Puducherry':                  { min: 3, max: 5 },
  'Chandigarh':                  { min: 3, max: 5 },
};

const DEFAULT_ETA = { min: 3, max: 7 };

const PINCODE_RE = /^[1-9][0-9]{5}$/;

/**
 * Look up a 6-digit Indian pincode and return serviceability + ETA.
 *
 * Returns one of:
 *   { ok: false, reason: 'invalid' }       — not a 6-digit pincode
 *   { ok: false, reason: 'unknown' }       — pincode not found in India Post DB
 *   { ok: false, reason: 'non-delivery' }  — all post offices marked Non Delivery
 *   { ok: false, reason: 'network', message } — fetch failed
 *   { ok: true,  city, state, etaMin, etaMax }
 */
export async function lookupPincode(rawCode) {
  const code = String(rawCode || '').trim();
  if (!PINCODE_RE.test(code)) {
    return { ok: false, reason: 'invalid' };
  }
  let payload;
  try {
    const res = await fetch(ENDPOINT(code), { method: 'GET' });
    if (!res.ok) {
      return { ok: false, reason: 'network', message: `HTTP ${res.status}` };
    }
    payload = await res.json();
  } catch (e) {
    return { ok: false, reason: 'network', message: e?.message || 'fetch failed' };
  }
  const row = Array.isArray(payload) ? payload[0] : null;
  const postOffices = row?.PostOffice || [];
  if (!row || row.Status !== 'Success' || postOffices.length === 0) {
    return { ok: false, reason: 'unknown' };
  }
  // If every post office at this pincode is Non Delivery, we can't ship there.
  const anyDelivers = postOffices.some(o => (o.DeliveryStatus || '').toLowerCase() !== 'non delivery');
  if (!anyDelivers) {
    return { ok: false, reason: 'non-delivery' };
  }
  const primary = postOffices[0];
  const state = primary.State || '';
  const city = primary.District || primary.Block || primary.Region || state;
  const eta = STATE_ETA[state] || DEFAULT_ETA;
  return {
    ok: true,
    city,
    state,
    etaMin: eta.min,
    etaMax: eta.max,
  };
}

const STORAGE_KEY = 'se_pincode_v1';

export function rememberPincode(code, result) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      code, result, at: Date.now(),
    }));
  } catch {}
}

export function recallPincode() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    // Expire after 30 days — pincodes don't change but customers move and
    // long-stale state on a shared device feels off.
    if (!obj || (Date.now() - (obj.at || 0)) > 30 * 24 * 3600 * 1000) return null;
    return obj;
  } catch {
    return null;
  }
}

// IP-based location detection — used to populate the topnav "Deliver to"
// label on first visit so it's not a blank "Select location". This is a
// city-level hint, NOT a delivery commitment — the user still needs to
// enter an actual pincode to get a real serviceability check + ETA.
//
// Stored under a separate key so a manual pincode (the source of truth for
// shipping) never gets clobbered by an IP guess.
const IP_LOCATION_KEY = 'se_ip_location_v1';

export async function detectLocationByIP() {
  try {
    const res = await fetch('https://ipapi.co/json/', { method: 'GET' });
    if (!res.ok) return { ok: false, reason: 'network' };
    const data = await res.json();
    // ipapi.co returns { error: true, reason } when rate-limited / restricted.
    if (!data || data.error) return { ok: false, reason: 'unknown' };
    // Only trust India hits — out-of-country IPs would mislabel the header
    // since we only ship within India.
    if ((data.country_code || data.country) !== 'IN') {
      return { ok: false, reason: 'out-of-country' };
    }
    const city = data.city || data.region || '';
    const state = data.region || '';
    if (!city) return { ok: false, reason: 'unknown' };
    return { ok: true, city, state };
  } catch (e) {
    return { ok: false, reason: 'network', message: e?.message || 'fetch failed' };
  }
}

export function rememberIpLocation(loc) {
  try {
    localStorage.setItem(IP_LOCATION_KEY, JSON.stringify({ loc, at: Date.now() }));
  } catch {}
}

export function recallIpLocation() {
  try {
    const raw = localStorage.getItem(IP_LOCATION_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    // 24h TTL — short enough that travelling users see the right city
    // on their next session, long enough to avoid hammering the API.
    if (!obj || (Date.now() - (obj.at || 0)) > 24 * 3600 * 1000) return null;
    return obj.loc;
  } catch {
    return null;
  }
}

// Browser Geolocation + reverse-geocoding (precise, requires permission).
// We call bigdatacloud's free reverse-geocode-client endpoint (no key, CORS
// enabled, designed for browser use) to turn lat/lon into city + state.
//
// Returns the same shape as detectLocationByIP() for a single drop-in path.
export async function detectLocationByGPS({ timeoutMs = 7000 } = {}) {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return { ok: false, reason: 'unsupported' };
  }
  // Skip the prompt entirely if the browser already remembers a denial —
  // avoids the "ask every time" loop on Chrome and respects the user's
  // earlier choice.
  try {
    if (navigator.permissions?.query) {
      const status = await navigator.permissions.query({ name: 'geolocation' });
      if (status.state === 'denied') return { ok: false, reason: 'denied' };
    }
  } catch { /* permissions API not supported — fall through to getCurrentPosition */ }

  const coords = await new Promise((resolve) => {
    let done = false;
    const finish = (val) => { if (!done) { done = true; resolve(val); } };
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => finish({ ok: true, lat: pos.coords.latitude, lon: pos.coords.longitude }),
        (err) => finish({ ok: false, reason: err.code === 1 ? 'denied' : 'unavailable' }),
        { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 5 * 60 * 1000 }
      );
    } catch {
      finish({ ok: false, reason: 'unavailable' });
    }
  });
  if (!coords.ok) return coords;

  try {
    const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${coords.lat}&longitude=${coords.lon}&localityLanguage=en`;
    const res = await fetch(url, { method: 'GET' });
    if (!res.ok) return { ok: false, reason: 'network' };
    const data = await res.json();
    if ((data.countryCode || '').toUpperCase() !== 'IN') {
      return { ok: false, reason: 'out-of-country' };
    }
    const city = data.city || data.locality || data.principalSubdivision || '';
    const state = data.principalSubdivision || '';
    if (!city) return { ok: false, reason: 'unknown' };
    return { ok: true, city, state };
  } catch (e) {
    return { ok: false, reason: 'network', message: e?.message || 'reverse-geocode failed' };
  }
}
